// Shared helpers for the bulk "Download Calibrated" flows.
//
// When the server cannot calibrate a frame it returns the RAW bytes with
// `X-Calibration-Status: unavailable` (see calibrationRoutes `format=both`). The
// client must never save those under a calibrated-looking name — otherwise the
// user keeps uncalibrated science data believing it is calibrated (#1276). These
// helpers mark such files and build a single end-of-run warning. Kept in one place
// so the three near-identical bulk flows can't drift.

/** True when a `format=both` response indicates the server fell back to raw. */
export function isUncalibratedResponse(calibrationStatusHeader) {
  return calibrationStatusHeader === 'unavailable';
}

/**
 * Insert an `_UNCALIBRATED` marker before the file extension so a raw frame saved
 * by the "Download Calibrated" flow is never mistaken for calibrated data.
 * Handles compound FITS extensions (`.fits.fz` / `.fit.fz`), single (`.fits` /
 * `.fit`), or none.
 *   "M31.fits.fz" -> "M31_UNCALIBRATED.fits.fz"
 *   "M31.fits"    -> "M31_UNCALIBRATED.fits"
 *   "M31"         -> "M31_UNCALIBRATED"
 */
export function markUncalibratedName(filename) {
  if (!filename) return 'UNCALIBRATED';
  const m = filename.match(/\.(fits|fit)(\.fz)?$/i);
  if (!m) return `${filename}_UNCALIBRATED`;
  const base = filename.slice(0, m.index);
  return `${base}_UNCALIBRATED${m[0]}`;
}

/**
 * Build a user-facing warning listing the frames saved raw because the server
 * could not calibrate them. Returns null when nothing was uncalibrated.
 */
export function buildUncalibratedWarning(uncalibratedNames) {
  if (!uncalibratedNames || uncalibratedNames.length === 0) return null;
  const n = uncalibratedNames.length;
  const was = n === 1 ? 'frame was' : 'frames were';
  return (
    `${n} ${was} not calibrated and ${n === 1 ? 'was' : 'were'} saved raw ` +
    `(marked _UNCALIBRATED): ${uncalibratedNames.join(', ')}. ` +
    `Re-run the download to retry those frames.`
  );
}
